import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  CheckCircle as ActiveIcon,
  Add as AddIcon,
  PauseCircleOutlined as DeactivateIcon,
  Edit as EditIcon,
  DoNotDisturb as InactiveIcon,
  PlayCircleOutlined as ReactivateIcon,
  Schedule as ScheduledIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { useGetPointsPurchaseOffers } from '~/hooks/apis/loyalty/useGetPointsPurchaseOffers';
import { useGetPointsPurchaseOffersPreview } from '~/hooks/apis/loyalty/useGetPointsPurchaseOffersPreview';
import { useUpdatePointsPurchaseOffer } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import { type PointsPurchaseOffer } from '~/types/loyaltyContract';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import CreateOfferDialog from './CreateOfferDialog';
import DeactivateConfirmDialog from './DeactivateConfirmDialog';
import EditOfferDialog from './EditOfferDialog';
import OffersPreviewTable from './OffersPreviewTable';
import PayForPointsToolbar from './PayForPointsToolbar';

const DEFAULT_PAGE_SIZE = 25;

function getOfferStatusChipColor(status: string): 'success' | 'default' | 'info' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SCHEDULED':
      return 'info';
    default:
      return 'default';
  }
}

interface OffersSectionProps {
  currency: string;
  onCurrencyChange: (currency: string) => void;
}

export default function OffersSection({ currency, onCurrencyChange }: OffersSectionProps) {
  const theme = useTheme();
  const updateMutation = useUpdatePointsPurchaseOffer();

  const [isPreview, setIsPreview] = useState(false);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<PointsPurchaseOffer | null>(null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    validFrom: false,
    createdAt: false,
    updatedAt: false,
  });

  const { data, isLoading, error } = useGetPointsPurchaseOffers(currency);
  const previewQuery = useGetPointsPurchaseOffersPreview(currency, isPreview);

  const allOffers: Array<PointsPurchaseOffer> = useMemo(() => data?.result ?? [], [data]);

  const statusCounts = useMemo(() => {
    const counts = { ACTIVE: 0, INACTIVE: 0, SCHEDULED: 0, total: 0 };
    for (const offer of allOffers) {
      if (offer.status in counts) {
        counts[offer.status]++;
      }
      counts.total++;
    }
    return counts;
  }, [allOffers]);

  const filteredOffers = useMemo(() => {
    if (statusFilter === 'all') {
      return allOffers;
    }
    return allOffers.filter((offer) => offer.status === statusFilter);
  }, [allOffers, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('all');
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handleOpenEdit = useCallback((offer: PointsPurchaseOffer) => {
    setSelectedOffer(offer);
    setIsEditOpen(true);
  }, []);

  const handleOpenDeactivate = useCallback((offer: PointsPurchaseOffer) => {
    setSelectedOffer(offer);
    setIsDeactivateOpen(true);
  }, []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!selectedOffer) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        offerId: selectedOffer.id,
        params: { status: 'INACTIVE' },
      });
      setIsDeactivateOpen(false);
      setSelectedOffer(null);
    } catch {
      // Error handled in mutation hook
    }
  }, [selectedOffer, updateMutation]);

  const handleReactivate = useCallback(
    async (offer: PointsPurchaseOffer) => {
      try {
        await updateMutation.mutateAsync({
          offerId: offer.id,
          params: { status: 'ACTIVE' },
        });
      } catch {
        // Error handled in mutation hook
      }
    },
    [updateMutation],
  );

  const columns: Array<GridColDef<PointsPurchaseOffer>> = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 120 },
      { field: 'title', headerName: 'Title', flex: 1, minWidth: 130 },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<PointsPurchaseOffer>) => (
          <Chip label={value} size="small" color={getOfferStatusChipColor(value)} />
        ),
      },
      {
        field: 'basePoints',
        headerName: 'Base Points',
        width: 120,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value: number) => value?.toLocaleString(),
      },
      {
        field: 'price',
        headerName: 'Price',
        width: 120,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (value: number) => (value != null ? value.toFixed(2) : '-'),
      },
      { field: 'currency', headerName: 'Currency', width: 90 },
      {
        field: 'validFrom',
        headerName: 'Valid From',
        width: 150,
        valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 130,
        valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 130,
        valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<PointsPurchaseOffer>) => {
          const isInactive = row.status === 'INACTIVE';
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" height="100%">
              <Tooltip title="Edit offer">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenEdit(row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isInactive ? (
                <Tooltip title="Reactivate offer">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleReactivate(row);
                    }}
                  >
                    <ReactivateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Deactivate offer">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenDeactivate(row);
                    }}
                  >
                    <DeactivateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [handleOpenEdit, handleOpenDeactivate, handleReactivate],
  );

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 'bold' }}>
              Points Purchase Offers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage pricing tiers for points purchases
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant={isPreview ? 'contained' : 'outlined'}
              startIcon={isPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => {
                setIsPreview((prev) => !prev);
                setStatusFilter('all');
              }}
              color={isPreview ? 'primary' : 'inherit'}
              aria-pressed={isPreview}
            >
              {isPreview ? 'Back to Admin View' : 'Customer Preview'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateOpen(true)}
              disabled={isPreview}
            >
              Create Offer
            </Button>
          </Stack>
        </Box>
      </Paper>

      {isPreview && (
        <Box mb={3}>
          <Alert severity="info">Preview mode — showing offers as customers see them, with campaigns applied</Alert>
        </Box>
      )}

      {!isPreview && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: theme.typography.fontWeightBold }}>
              Status:
            </Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_e, newValue) => {
                if (newValue !== null) {
                  setStatusFilter(newValue);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                    color: 'primary.main',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.25),
                    },
                  },
                  '&:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                  },
                },
              }}
            >
              <ToggleButton value="all">All ({statusCounts.total})</ToggleButton>
              <ToggleButton value="ACTIVE">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ActiveIcon sx={{ fontSize: 'body2.fontSize', color: 'success.main' }} />
                  <Typography variant="body2" component="span">
                    Active ({statusCounts.ACTIVE})
                  </Typography>
                </Stack>
              </ToggleButton>
              <ToggleButton value="SCHEDULED">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ScheduledIcon sx={{ fontSize: 'body2.fontSize', color: 'info.main' }} />
                  <Typography variant="body2" component="span">
                    Scheduled ({statusCounts.SCHEDULED})
                  </Typography>
                </Stack>
              </ToggleButton>
              <ToggleButton value="INACTIVE">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <InactiveIcon sx={{ fontSize: 'body2.fontSize', color: 'grey.400' }} />
                  <Typography variant="body2" component="span">
                    Inactive ({statusCounts.INACTIVE})
                  </Typography>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>
      )}

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            label="Currency"
            value={currency}
            onChange={(event) => onCurrencyChange(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            {getAllCurrencyCodes().map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          {!isPreview && (
            <Button variant="text" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {isPreview ? (
        <OffersPreviewTable
          offers={previewQuery.data?.result ?? []}
          isLoading={previewQuery.isLoading}
          error={previewQuery.error}
        />
      ) : (
        <>
          {error && (
            <Box mb={3}>
              <Alert severity="error">
                Error loading offers: {error instanceof Error ? error.message : String(error)}
              </Alert>
            </Box>
          )}

          <Paper elevation={0} sx={{ height: 650 }}>
            <DataGridPro
              rows={filteredOffers}
              columns={columns}
              loading={isLoading}
              pagination
              paginationMode="client"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: 'status', sort: 'asc' }] },
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              slots={{ toolbar: PayForPointsToolbar }}
              sx={{
                height: '100%',
                '& .MuiDataGrid-row:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                },
              }}
            />
          </Paper>
        </>
      )}

      <CreateOfferDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} defaultCurrency={currency} />

      {selectedOffer && (
        <EditOfferDialog
          open={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedOffer(null);
          }}
          offer={selectedOffer}
        />
      )}

      <DeactivateConfirmDialog
        open={isDeactivateOpen}
        onClose={() => {
          setIsDeactivateOpen(false);
          setSelectedOffer(null);
        }}
        onConfirm={handleConfirmDeactivate}
        isPending={updateMutation.isPending}
        entityType="offer"
        entityLabel={selectedOffer?.title ?? ''}
      />
    </Box>
  );
}
